import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { Reveal } from "./anim";
import { RevealText, CustomCursor } from "./scroll";
import { SmoothScroll } from "./experience";
import { POSTS } from "./blogData";
import { IMAGES as IMG, breadcrumb, SITE } from "./marketingData";

export default function BlogIndex() {
  const [featured, ...rest] = POSTS;
  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden md:[cursor:none]">
      <CustomCursor />
      <SmoothScroll />
      <Seo
        title="Insights & Guides on Heavy Transport in India | ITO"
        description="Expert guides on ODC, defence, air-suspension trailers, explosive vans and industrial tank transport across India — from Indian Transport Organisation, since 1972."
        path="/insights"
        image={IMG.blogBanner}
        keywords="logistics insights india, transport blog india, odc transport guides, freight tips, heavy haul articles"
        jsonLd={[
          breadcrumb([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }]),
          {
            "@context": "https://schema.org", "@type": "Blog",
            name: "ITO Insights", url: `${SITE}/insights`,
            blogPost: POSTS.map((p) => ({
              "@type": "BlogPosting", headline: p.title, url: `${SITE}/insights/${p.slug}`,
              datePublished: p.date, image: p.image,
            })),
          },
        ]}
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Insights &amp; guides</p>
          <RevealText as="h1" className="font-heading font-black tracking-tighter text-4xl md:text-6xl max-w-4xl leading-[1.03]"
            text="Field notes from India's heaviest roads." />
          <p className="mt-5 text-lg text-[#4F4C44] max-w-2xl">
            Practical guides on moving ODC, defence, machinery and tanks across India — written by the
            team that does it every day.
          </p>
        </div>
      </section>

      {/* FEATURED */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Link to={`/insights/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} data-cursor
              className="group grid lg:grid-cols-2 gap-8 rounded-3xl bg-white border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-xl transition-all duration-300">
              <div className="relative h-64 lg:h-full min-h-[280px] overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-5 left-5 rounded-full bg-[#C84B31] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">{featured.tag}</span>
              </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <div className="text-xs text-[#4F4C44] mb-3">{featured.readTime}</div>
                <h2 className="font-heading font-bold tracking-tight text-2xl md:text-3xl leading-tight">{featured.title}</h2>
                <p className="mt-4 text-[#4F4C44] leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                  Read guide <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link to={`/insights/${p.slug}`} data-testid={`blog-card-${p.slug}`} data-cursor
                className="group block h-full rounded-2xl bg-white border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="relative h-44 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <span className="absolute top-4 left-4 rounded-full bg-[#C84B31] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">{p.tag}</span>
                </div>
                <div className="p-6">
                  <div className="text-xs text-[#4F4C44] mb-2">{p.readTime}</div>
                  <h3 className="font-heading font-semibold text-lg leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#4F4C44] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                    Read guide <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
